import { trackJourney } from "./business-metrics"
import { addMonitorBreadcrumb } from "./monitor-breadcrumb"
import { serializeError } from "./error-serializer"
import {
  buildEventFingerprint,
  isIgnoredErrorMessage,
  shouldDedupeEvent,
} from "./sentry-filters"
import { captureException } from "./sentry-state"

const SPINNER_SELECTOR = "[data-testid='spinner-screen']"

export function startHealthMonitor() {
  if (typeof window === "undefined") return

  requestAnimationFrame(() => {
    window.setTimeout(() => {
      const app = document.getElementById("app")
      const isBlank = !app?.innerHTML?.trim() || app.children.length === 0

      if (!isBlank) return

      const fingerprint = buildEventFingerprint(["health", "white_screen"])
      if (shouldDedupeEvent(fingerprint)) return

      trackJourney("white_screen", "fail", { reason: "empty_app_root" })
    }, 5000)
  })

  let spinnerVisibleSince: number | null = null

  const observer = new MutationObserver(() => {
    const spinner = document.querySelector(SPINNER_SELECTOR)

    if (spinner) {
      spinnerVisibleSince ??= Date.now()

      if (Date.now() - spinnerVisibleSince > 15000) {
        const fingerprint = buildEventFingerprint([
          "health",
          "infinite_loading",
        ])
        if (!shouldDedupeEvent(fingerprint)) {
          trackJourney("infinite_loading", "fail", {
            duration_ms: Date.now() - spinnerVisibleSince,
          })
        }
        spinnerVisibleSince = null
      }

      return
    }

    spinnerVisibleSince = null
  })

  observer.observe(document.body, { childList: true, subtree: true })

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason
    const serialized = serializeError(reason)
    const message = serialized.message

    if (isIgnoredErrorMessage(message)) return

    const fingerprint = buildEventFingerprint([
      "unhandled_rejection",
      serialized.api,
      serialized.errorCode,
      message,
    ])

    if (shouldDedupeEvent(fingerprint)) return

    addMonitorBreadcrumb({
      category: "unhandled_rejection",
      level: "error",
      message: serialized.errorCode
        ? `[${serialized.api ?? "promise"}] ${serialized.errorCode}: ${message}`
        : message,
      data: serialized as Record<string, unknown>,
    })
  })

  window.addEventListener("error", (event) => {
    if (isIgnoredErrorMessage(event.message)) return

    captureException(event.error ?? new Error(event.message), {
      tags: { feature: "window_error" },
    })
  })
}
